const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const isGroupedInteger = (text, separator) =>
    new RegExp(`^[1-9]\\d{0,2}(?:${escapeRegExp(separator)}\\d{3})+$`).test(text)

const SYMBOL_LOCALES = ['en-GB', 'en-US', 'de-DE']

const currencySymbols = (locale, currencyCode) => {
    const symbols = new Set()

    for (const symbolLocale of [locale, ...SYMBOL_LOCALES]) {
        for (const currencyDisplay of ['symbol', 'narrowSymbol']) {
            try {
                const symbol = new Intl.NumberFormat(symbolLocale, {
                    style: 'currency',
                    currency: currencyCode,
                    currencyDisplay,
                })
                    .formatToParts(1)
                    .find((part) => part.type === 'currency')?.value

                if (symbol) {
                    symbols.add(symbol)
                }
            } catch {
                // Invalid locale or currency code: nothing to strip for it
            }
        }
    }

    return [...symbols].sort((a, b) => b.length - a.length)
}

const stripCurrency = (text, locale, currencyCode) => {
    if (!currencyCode) {
        return text
    }

    const withoutCode = text.replace(new RegExp(escapeRegExp(currencyCode), 'gi'), '')

    return currencySymbols(locale, currencyCode).reduce(
        (result, symbol) => result.split(symbol).join(''),
        withoutCode,
    )
}

// "45,000" is 45 or 45000: a field that can't hold 3 decimals can only mean thousands
const canOnlyBeThousands = (maxFractionDigits) =>
    Number.isInteger(maxFractionDigits) && maxFractionDigits < 3

const normalizeSeparators = (digits, maxFractionDigits) => {
    const lastDot = digits.lastIndexOf('.')
    const lastComma = digits.lastIndexOf(',')

    if (lastDot !== -1 && lastComma !== -1) {
        const decimal = lastDot > lastComma ? '.' : ','
        const group = decimal === '.' ? ',' : '.'
        const parts = digits.split(decimal)

        if (parts.length !== 2 || !isGroupedInteger(parts[0], group)) {
            return null
        }

        return `${parts[0].split(group).join('')}.${parts[1]}`
    }

    const separator = lastDot !== -1 ? '.' : lastComma !== -1 ? ',' : null

    if (separator === null) {
        return digits
    }

    if (digits.split(separator).length > 2) {
        return isGroupedInteger(digits, separator) ? digits.split(separator).join('') : null
    }

    if (isGroupedInteger(digits, separator)) {
        return canOnlyBeThousands(maxFractionDigits) ? digits.split(separator).join('') : null
    }

    return digits.replace(separator, '.')
}

export const parsePastedNumber = (text, locale, currencyCode = null, maxFractionDigits = null) => {
    if (typeof text !== 'string') {
        return null
    }

    const cleaned = stripCurrency(text.trim(), locale, currencyCode)
        .replace(/[\s'’]/g, '')
        .replace(/−/g, '-')

    const match = cleaned.match(/^([-+]?)([\d.,]*\d[\d.,]*)$/)

    if (!match) {
        return null
    }

    const normalized = normalizeSeparators(match[2], maxFractionDigits)

    if (normalized === null) {
        return null
    }

    const value = Number(match[1] + normalized)

    return Number.isFinite(value) ? value : null
}
